import { QK } from '@/js/consts/query-keys';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
    migrateCompany,
    migrateSystem,
    getUnmigratedCompanies,
    getUnmigratedExternalSystems,
    getMigratedTenants,
    getMigratedExternalSystems,
    getTenants,
    seedAllTenantData,
    deleteTenant,
    getCompanies,
    getKeyIndicateUsers,
    getAssignableTenantRoles,
    importUsers,
    getAgentsWithoutUser,
    enqueueCreateUsersFromAgents,
    getCreateUserFromAgentJobs,
    getTenantConfigOptions,
    importTenantConfig,
    updateOrgTree,
    getImportIntegrationConfigOptions,
    importIntegrationConfig,
    getIntegrationConfigOptions,
    getIntegrationConfigs,
    getIntegrationConfig,
    saveIntegrationConfig,
    deleteIntegrationConfig,
    createTenant,
    getExternalSystemsForTenant,
    syncRawDataProcessor,
    syncCoachingData,
    syncAiInsightsData,
    getSyncJobs,
    getIntegrationOverview,
    enqueueIntegrationManualSync,
    getIntegrationSyncJobs,
    getCsmUsers,
    getSysOpUsers,
    createSysOpUser,
    changeUserRole,
    getTenant,
    updateTenant,
} from './backOfficeApi';
import { testGenesysConnection } from './genesysApi';
import {
    getStreamingProviderOptions,
    getStreamingConfigs,
    getStreamingConfig,
    getStreamingTenantFilterOptions,
    createStreamingConfig,
    updateStreamingConfig,
    deleteStreamingConfig,
} from './streamingApi';
import {
    getAsrConfigRules,
    getAsrConfigFilterOptions,
    getAsrConfigExternalSystems,
    createAsrConfigRule,
    updateAsrConfigRule,
    deleteAsrConfigRule,
    type AsrConfigRuleInput,
    type AsrChannelType,
} from './asrConfigApi';
import {
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    type CreateCategoryInput,
    type UpdateCategoryInput,
} from './categoriesApi';
import {
    getRetentionSettings,
    upsertRetentionSetting,
    deleteRetentionSetting,
    getRetentionDataTypes,
    getRetentionQueuesAndSkills,
    type UpsertRetentionSettingInput,
} from './retentionSettingsApi';
import {
    getKeyIndicateSyncSystems,
    saveKeyIndicateSyncSystemSettings,
    getKeyIndicateSyncDataTypes,
    setKeyIndicateSyncDataTypeEnabled,
    type KeyIndicateSyncDataType,
    type KeyIndicateSyncSetting,
} from './keyIndicateSyncSettingsApi';
import {
    enqueueAsrFullAnalysis,
    sendInteractionsForAnalysisByInterval,
    getAsrJobs,
    getIntervalAnalysisJobs,
    type EnqueueAsrFullAnalysisResponse,
    type AsrJobStatus,
} from './asrEnqueueApi';
import {
    triggerBackfillAnalysisReceivedAt,
    getBackfillAnalysisReceivedAtJobs,
} from './backfillAnalysisReceivedAtApi';
import { useGetMyAccount } from '@/js/hooks/user';
import { useToastStore } from '@/js/stores/snackbar';
import { __ } from '@/js/stores/expressions';
import { computed, toValue, type Ref, type MaybeRefOrGetter } from 'vue';
import type { AxiosError } from 'axios';
import { SyncJobStatus } from './types';
import type {
    RequestMigrateCompany,
    RequestMigrateSystem,
    RequestImportUsers,
    RequestImportTenantConfig,
    RequestUpdateOrgTree,
    ResponseUpdateOrgTree,
    RequestImportIntegrationConfig,
    RequestSaveIntegrationConfig,
    RequestCreateTenant,
    RequestManualSync,
    ManualSyncResult,
    RequestTestGenesysConnection,
    SyncJob,
    RequestCreateSysOpUser,
    RequestChangeUserRole,
    ApiTenant,
    RequestEnqueueCreateUsersFromAgents,
    ApiCreateUserFromAgentJobEntry,
    OneOffJobEntry,
    OneOffJobTriggerResponse,
} from './types';

const ACTIVE_SYNC_JOB_STATUSES = [SyncJobStatus.Waiting, SyncJobStatus.Running];

const refetchIntervalWhileJobsActive = (jobs: SyncJob[] | undefined) => {
    if (!Array.isArray(jobs)) return false;
    return jobs.some((j) => ACTIVE_SYNC_JOB_STATUSES.includes(j.status)) ? 5000 : false;
};

export const useQueryGetBackOfficeTenants = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.TENANTS],
        queryFn: async () => getTenants(),
        select: (response) => Object.values(response),
        enabled,
    });
};

export const useQueryGetBackOfficeTenant = (id: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!id.value && !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.TENANT, id.value]),
        queryFn: async () => getTenant(id.value),
        enabled,
    });
};

export function useMutationUpdateBackOfficeTenant() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: ApiTenant) => updateTenant(data),
        onSuccess: (_, req) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.TENANTS],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.TENANT, req.id],
            });
        },
    });
}

export const useQueryGetUnmigratedCompanies = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.UNMIGRATED_COMPANIES],
        queryFn: async () => getUnmigratedCompanies(),
        enabled,
    });
};

export const useQueryGetUnmigratedExternalSystems = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.UNMIGRATED_EXTERNAL_SYSTEMS],
        queryFn: async () => getUnmigratedExternalSystems(),
        enabled,
    });
};

export const useQueryGetMigratedTenants = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.MIGRATED_TENANTS],
        queryFn: async () => getMigratedTenants(),
        enabled,
    });
};

export const useQueryGetMigratedExternalSystems = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.MIGRATED_EXTERNAL_SYSTEMS],
        queryFn: async () => getMigratedExternalSystems(),
        enabled,
    });
};

export const useMutationMigrateCompany = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestMigrateCompany) => migrateCompany(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.BACK_OFFICE.TENANTS] });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.UNMIGRATED_COMPANIES],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.MIGRATED_TENANTS],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.UNMIGRATED_EXTERNAL_SYSTEMS],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.MIGRATED_EXTERNAL_SYSTEMS],
            });
        },
    });
};

export const useMutationMigrateSystem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestMigrateSystem) => migrateSystem(data),
        onSuccess: (_, req) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.MIGRATED_TENANTS],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.UNMIGRATED_COMPANIES],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.UNMIGRATED_EXTERNAL_SYSTEMS],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.MIGRATED_EXTERNAL_SYSTEMS],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.EXTERNAL_SYSTEMS_FOR_TENANT, req.tenantId],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.KEY_INDICATE_SYNC_SYSTEMS, req.tenantId],
            });
        },
    });
};

export const useMutationSeedAllTenantData = () => {
    return useMutation({
        mutationFn: async () => seedAllTenantData(),
    });
};

export const useQueryGetCompanies = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.COMPANIES],
        queryFn: async () => getCompanies(),
        enabled,
    });
};

export const useQueryGetKeyIndicateUsers = (tenantId: Ref<string>, fetchEnabled?: Ref<boolean>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(
        () =>
            !!account.value?.backOfficeApiBaseUrl &&
            !!tenantId.value &&
            (fetchEnabled ? fetchEnabled.value : true)
    );

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.KEY_INDICATE_USERS, tenantId.value]),
        queryFn: async () => getKeyIndicateUsers(tenantId.value),
        enabled,
    });
};

// Excludes SysOp - use wherever the role gets assigned as part of a customer's own config.
export const useQueryGetAssignableTenantRoles = (tenantName: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantName.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.ASSIGNABLE_TENANT_ROLES, tenantName.value]),
        queryFn: async () => getAssignableTenantRoles(tenantName.value),
        enabled,
    });
};

export const useMutationImportUsers = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, RequestImportUsers>({
        mutationFn: async (data) => importUsers(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.KEY_INDICATE_USERS],
            });
        },
    });
};

export const useQueryGetAgentsWithoutUser = (
    tenantId: Ref<string>,
    fetchEnabled?: Ref<boolean>
) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(
        () =>
            !!account.value?.backOfficeApiBaseUrl &&
            !!tenantId.value &&
            (fetchEnabled ? fetchEnabled.value : true)
    );

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.AGENTS_WITHOUT_USER, tenantId.value]),
        queryFn: async () => getAgentsWithoutUser(tenantId.value),
        enabled,
    });
};

// Queues user creation instead of creating synchronously - safe for a batch of any size.
export const useMutationEnqueueCreateUsersFromAgents = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestEnqueueCreateUsersFromAgents) =>
            enqueueCreateUsersFromAgents(data),
        onSuccess: (_, req) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.AGENTS_WITHOUT_USER, req.tenantId],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.CREATE_USER_FROM_AGENT_JOBS, req.tenantId],
            });
        },
    });
};

const ACTIVE_CREATE_USER_FROM_AGENT_JOB_STATUSES = [SyncJobStatus.Waiting, SyncJobStatus.Running];

export const useQueryGetCreateUserFromAgentJobs = (tenantId: MaybeRefOrGetter<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!toValue(tenantId));

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.CREATE_USER_FROM_AGENT_JOBS, toValue(tenantId)]),
        queryFn: async () => getCreateUserFromAgentJobs(toValue(tenantId)),
        enabled,
        refetchInterval: (query) => {
            const jobs = query.state.data as ApiCreateUserFromAgentJobEntry[] | undefined;
            if (!Array.isArray(jobs)) return false;
            return jobs.some((j) => ACTIVE_CREATE_USER_FROM_AGENT_JOB_STATUSES.includes(j.status))
                ? 5000
                : false;
        },
    });
};

const ACTIVE_ONE_OFF_JOB_STATUSES = [SyncJobStatus.Waiting, SyncJobStatus.Running];

/** Polls a one-off job's runs while any of them is still queued or running. */
const pollWhileOneOffJobsActive = (query: { state: { data: unknown } }) => {
    const jobs = query.state.data as OneOffJobEntry[] | undefined;
    if (!Array.isArray(jobs)) return false;
    return jobs.some((j) => ACTIVE_ONE_OFF_JOB_STATUSES.includes(j.status)) ? 5000 : false;
};

export const useQueryGetBackfillAnalysisReceivedAtJobs = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.BACKFILL_ANALYSIS_RECEIVED_AT_JOBS],
        queryFn: async () => getBackfillAnalysisReceivedAtJobs(),
        enabled,
        refetchInterval: pollWhileOneOffJobsActive,
    });
};

export const useMutationTriggerBackfillAnalysisReceivedAt = () =>
    useMutation<OneOffJobTriggerResponse, Error, void>({
        mutationFn: async () => triggerBackfillAnalysisReceivedAt(),
    });

export const useQueryGetMigrateTenantConfigOptions = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.MIGRATE_TENANT_CONFIG_OPTIONS],
        queryFn: async () => getTenantConfigOptions(),
        enabled,
    });
};

export const useQueryGetImportTenantConfigOptions = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.IMPORT_TENANT_CONFIG_OPTIONS, tenantId.value]),
        queryFn: async () => getTenantConfigOptions(tenantId.value),
        enabled,
    });
};

export const useQueryGetImportIntegrationConfigOptions = (
    tenantId: Ref<string>,
    externalSystemId: Ref<string>
) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(
        () => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value && !!externalSystemId.value
    );

    return useQuery({
        queryKey: computed(() => [
            QK.BACK_OFFICE.IMPORT_INTEGRATION_CONFIG_OPTIONS,
            tenantId.value,
            externalSystemId.value,
        ]),
        queryFn: async () =>
            getImportIntegrationConfigOptions(tenantId.value, externalSystemId.value),
        enabled,
    });
};

export const useQueryGetMigrateSystemConfigOptions = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.MIGRATE_SYSTEM_CONFIG_OPTIONS, tenantId.value]),
        queryFn: async () => getImportIntegrationConfigOptions(tenantId.value),
        enabled,
    });
};

export const useMutationImportIntegrationConfig = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, RequestImportIntegrationConfig>({
        mutationFn: async (data) => importIntegrationConfig(data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [
                    QK.BACK_OFFICE.IMPORT_INTEGRATION_CONFIG_OPTIONS,
                    variables.tenantId,
                    variables.externalSystemId,
                ],
            });
        },
    });
};

export const useMutationImportTenantConfig = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, RequestImportTenantConfig>({
        mutationFn: async (data) => importTenantConfig(data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.IMPORT_TENANT_CONFIG_OPTIONS, variables.tenantId],
            });
        },
    });
};

export const useMutationUpdateOrgTree = () => {
    return useMutation<ResponseUpdateOrgTree, Error, RequestUpdateOrgTree>({
        mutationFn: async (data) => updateOrgTree(data),
    });
};

export const useQueryGetIntegrationConfigOptions = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.INTEGRATION_CONFIG_OPTIONS],
        queryFn: async () => getIntegrationConfigOptions(),
        enabled,
    });
};

export const useQueryGetIntegrationOverview = (systemKey: string) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.INTEGRATION_OVERVIEW, systemKey],
        queryFn: async () => getIntegrationOverview(systemKey),
        enabled,
    });
};

export const useQueryGetIntegrationConfigs = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.INTEGRATION_CONFIGS, tenantId.value]),
        queryFn: async () => getIntegrationConfigs(tenantId.value),
        enabled,
    });
};

export const useQueryGetIntegrationConfig = (tenantId: Ref<string>, systemKey: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(
        () => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value && !!systemKey.value
    );

    return useQuery({
        queryKey: computed(() => [
            QK.BACK_OFFICE.INTEGRATION_CONFIG,
            tenantId.value,
            systemKey.value,
        ]),
        queryFn: async () => getIntegrationConfig(tenantId.value, systemKey.value),
        enabled,
    });
};

export const useMutationSaveIntegrationConfig = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, RequestSaveIntegrationConfig>({
        mutationFn: async (data) => saveIntegrationConfig(data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [
                    QK.BACK_OFFICE.INTEGRATION_CONFIG,
                    variables.tenantId,
                    variables.systemKey,
                ],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.INTEGRATION_CONFIGS, variables.tenantId],
            });
        },
    });
};

export const useMutationDeleteIntegrationConfig = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { tenantId: string; systemKey: string }>({
        mutationFn: async ({ tenantId, systemKey }) => deleteIntegrationConfig(tenantId, systemKey),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [
                    QK.BACK_OFFICE.INTEGRATION_CONFIG,
                    variables.tenantId,
                    variables.systemKey,
                ],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.INTEGRATION_CONFIGS, variables.tenantId],
            });
        },
    });
};

export const useQueryGetStreamingTenantFilterOptions = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.STREAMING_TENANT_FILTER_OPTIONS, tenantId.value]),
        queryFn: async () => getStreamingTenantFilterOptions(tenantId.value),
        enabled,
    });
};

export const useQueryGetStreamingConfigs = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.STREAMING_CONFIGS, tenantId.value]),
        queryFn: async () => getStreamingConfigs(tenantId.value),
        enabled,
    });
};

export const useQueryGetStreamingConfig = (
    tenantId: Ref<string>,
    endpoint: Ref<string>,
    providerId: Ref<string>
) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(
        () =>
            !!account.value?.backOfficeApiBaseUrl &&
            !!tenantId.value &&
            !!endpoint.value &&
            !!providerId.value
    );

    return useQuery({
        queryKey: computed(() => [
            QK.BACK_OFFICE.STREAMING_CONFIG,
            tenantId.value,
            endpoint.value,
            providerId.value,
        ]),
        queryFn: async () => getStreamingConfig(endpoint.value, tenantId.value, providerId.value),
        enabled,
    });
};

export const useQueryGetStreamingProviderOptions = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.STREAMING_PROVIDER_OPTIONS],
        queryFn: async () => getStreamingProviderOptions(),
        enabled,
    });
};

export const useMutationCreateStreamingConfig = () => {
    const queryClient = useQueryClient();
    return useMutation<
        void,
        Error,
        { endpoint: string; tenantId: string; data: Record<string, unknown> }
    >({
        mutationFn: async ({ endpoint, data }) => createStreamingConfig(endpoint, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.STREAMING_CONFIGS, variables.tenantId],
            });
        },
    });
};

export const useMutationDeleteStreamingConfig = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { providerId: string; tenantId: string }>({
        mutationFn: async ({ providerId }) => deleteStreamingConfig(providerId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.STREAMING_CONFIGS, variables.tenantId],
            });
        },
    });
};

export const useMutationUpdateStreamingConfig = () => {
    const queryClient = useQueryClient();
    return useMutation<
        void,
        Error,
        { endpoint: string; providerId: string; tenantId: string; data: Record<string, unknown> }
    >({
        mutationFn: async ({ endpoint, providerId, data }) =>
            updateStreamingConfig(endpoint, providerId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.STREAMING_CONFIGS, variables.tenantId],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    QK.BACK_OFFICE.STREAMING_CONFIG,
                    variables.tenantId,
                    variables.endpoint,
                    variables.providerId,
                ],
            });
        },
    });
};

export const useMutationDeleteTenant = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, string>({
        mutationFn: async (tenantId) => deleteTenant(tenantId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.BACK_OFFICE.TENANTS] });
        },
    });
};

export const useMutationCreateTenant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestCreateTenant) => createTenant(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.BACK_OFFICE.TENANTS] });
        },
    });
};

export const useQueryGetExternalSystemsForTenant = (tenantId: Ref<string | null>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.EXTERNAL_SYSTEMS_FOR_TENANT, tenantId.value]),
        queryFn: () => getExternalSystemsForTenant(tenantId.value!),
        enabled,
    });
};

export const useMutationSyncRawDataProcessor = () =>
    useMutation({ mutationFn: syncRawDataProcessor });

export const useMutationSyncCoachingData = () => useMutation({ mutationFn: syncCoachingData });

export const useMutationSyncAiInsightsData = () => useMutation({ mutationFn: syncAiInsightsData });

export const useQueryGetSyncJobs = (
    jobName: MaybeRefOrGetter<string>,
    extraEnabled: MaybeRefOrGetter<boolean> = true
) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && toValue(extraEnabled));

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.SYNC_JOBS, toValue(jobName)]),
        queryFn: () => getSyncJobs(toValue(jobName)),
        enabled,
        refetchInterval: (query) => refetchIntervalWhileJobsActive(query.state.data),
    });
};

export const useMutationTestGenesysConnection = () =>
    useMutation<void, Error, RequestTestGenesysConnection>({
        mutationFn: async (data) => testGenesysConnection(data),
    });

export const useMutationEnqueueIntegrationManualSync = (systemKey: string) =>
    useMutation<ManualSyncResult, Error, RequestManualSync>({
        mutationFn: async (data) => enqueueIntegrationManualSync(systemKey, data),
    });

export const useQueryGetIntegrationSyncJobs = (systemKey: string) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.INTEGRATION_SYNC_JOBS, systemKey],
        queryFn: () => getIntegrationSyncJobs(systemKey),
        enabled,
        refetchInterval: (query) => refetchIntervalWhileJobsActive(query.state.data),
    });
};

export const useQueryGetIntervalAnalysisJobs = (extraEnabled: MaybeRefOrGetter<boolean> = true) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && toValue(extraEnabled));

    return useQuery({
        queryKey: [QK.BACK_OFFICE.SYNC_JOBS, 'interval-jobs'],
        queryFn: () => getIntervalAnalysisJobs(),
        enabled,
        refetchInterval: (query) => refetchIntervalWhileJobsActive(query.state.data),
    });
};

export const useQueryGetAsrConfigRules = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.ASR_CONFIG_RULES, tenantId.value]),
        queryFn: async () => getAsrConfigRules(tenantId.value),
        enabled,
    });
};

export const useQueryGetAsrConfigFilterOptions = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.ASR_CONFIG_FILTER_OPTIONS, tenantId.value]),
        queryFn: async () => getAsrConfigFilterOptions(tenantId.value),
        enabled,
    });
};

export const useQueryGetAsrConfigExternalSystems = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.ASR_CONFIG_EXTERNAL_SYSTEMS, tenantId.value]),
        queryFn: async () => getAsrConfigExternalSystems(tenantId.value),
        enabled,
    });
};

// Shows the backend's raw validation detail because this config only lives in BackOffice
// today (internal staff, technical detail is fine). Stop doing this once customers can
// manage these rules themselves - use a proper user-facing message instead.
const showAsrConfigRuleError = (error: unknown) => {
    const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
    useToastStore().addToast('error', detail ?? __('error-response'));
};

export const useMutationCreateAsrConfigRule = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { tenantId: string; data: AsrConfigRuleInput }>({
        mutationFn: async ({ tenantId, data }) => {
            await createAsrConfigRule(tenantId, data);
        },
        onError: showAsrConfigRuleError,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.ASR_CONFIG_RULES, variables.tenantId],
            });
        },
    });
};

export const useMutationUpdateAsrConfigRule = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { tenantId: string; id: string; data: AsrConfigRuleInput }>({
        mutationFn: async ({ tenantId, id, data }) => {
            await updateAsrConfigRule(tenantId, id, data);
        },
        onError: showAsrConfigRuleError,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.ASR_CONFIG_RULES, variables.tenantId],
            });
        },
    });
};

export const useMutationDeleteAsrConfigRule = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { tenantId: string; id: string }>({
        mutationFn: async ({ tenantId, id }) => deleteAsrConfigRule(tenantId, id),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.ASR_CONFIG_RULES, variables.tenantId],
            });
        },
    });
};

export const useQueryGetCategories = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.CATEGORIES, tenantId.value]),
        queryFn: async () => getCategories(tenantId.value),
        enabled,
    });
};

const showCategoryError = (error: unknown) => {
    const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
    useToastStore().addToast('error', detail ?? __('error-response'));
};

const showAsrEnqueueError = (error: unknown) => {
    const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
    useToastStore().addToast('error', detail ?? __('error-response'));
};

export const useMutationCreateCategory = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { tenantId: string; data: CreateCategoryInput }>({
        mutationFn: async ({ tenantId, data }) => {
            await createCategory(tenantId, data);
        },
        onError: showCategoryError,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.CATEGORIES, variables.tenantId],
            });
        },
    });
};

export const useMutationUpdateCategory = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { tenantId: string; id: number; data: UpdateCategoryInput }>({
        mutationFn: async ({ tenantId, id, data }) => {
            await updateCategory(tenantId, id, data);
        },
        onError: showCategoryError,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.CATEGORIES, variables.tenantId],
            });
        },
    });
};

// No onError here - a 409 means the category is still linked to transcriptions, and the
// caller (BackOfficeCategories.vue) handles that itself by re-asking with confirm=true instead
// of showing a generic error toast.
export const useMutationDeleteCategory = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { tenantId: string; id: number; confirm: boolean }>({
        mutationFn: async ({ tenantId, id, confirm }) => deleteCategory(tenantId, id, confirm),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.CATEGORIES, variables.tenantId],
            });
        },
    });
};

export const useQueryGetRetentionSettings = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.RETENTION_SETTINGS, tenantId.value]),
        queryFn: async () => getRetentionSettings(tenantId.value),
        enabled,
    });
};

export const useQueryGetRetentionDataTypes = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.RETENTION_DATA_TYPES],
        queryFn: getRetentionDataTypes,
        enabled,
    });
};

export const useQueryGetRetentionQueuesAndSkills = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.RETENTION_QUEUES_AND_SKILLS, tenantId.value]),
        queryFn: async () => getRetentionQueuesAndSkills(tenantId.value),
        enabled,
    });
};

const showRetentionSettingError = (error: unknown) => {
    const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
    useToastStore().addToast('error', detail ?? __('error-response'));
};

export const useMutationUpsertRetentionSetting = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { tenantId: string; data: UpsertRetentionSettingInput }>({
        mutationFn: async ({ tenantId, data }) => {
            await upsertRetentionSetting(tenantId, data);
        },
        onError: showRetentionSettingError,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.RETENTION_SETTINGS, variables.tenantId],
            });
        },
    });
};

export const useMutationDeleteRetentionSetting = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { tenantId: string; id: number }>({
        mutationFn: async ({ tenantId, id }) => deleteRetentionSetting(tenantId, id),
        onError: showRetentionSettingError,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.RETENTION_SETTINGS, variables.tenantId],
            });
        },
    });
};

export const useQueryGetKeyIndicateSyncSystems = (tenantId: Ref<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!tenantId.value);

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.KEY_INDICATE_SYNC_SYSTEMS, tenantId.value]),
        queryFn: async () => getKeyIndicateSyncSystems(tenantId.value),
        enabled,
    });
};

export const useQueryGetKeyIndicateSyncDataTypes = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.KEY_INDICATE_SYNC_DATA_TYPES],
        queryFn: getKeyIndicateSyncDataTypes,
        enabled,
    });
};

const showKeyIndicateSyncSettingError = (error: unknown) => {
    const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
    useToastStore().addToast('error', detail ?? __('error-response'));
};

export const useMutationSaveKeyIndicateSyncSystemSettings = () => {
    const queryClient = useQueryClient();
    return useMutation<
        void,
        Error,
        { tenantId: string; tenantExternalSystemId: string; settings: KeyIndicateSyncSetting[] }
    >({
        mutationFn: async ({ tenantExternalSystemId, settings }) =>
            saveKeyIndicateSyncSystemSettings(tenantExternalSystemId, settings),
        onError: showKeyIndicateSyncSettingError,
        // Returning the promise keeps the save pending until the refetch is done, so the next save sees fresh rows.
        onSuccess: (_, variables) =>
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.KEY_INDICATE_SYNC_SYSTEMS, variables.tenantId],
            }),
    });
};

export const useMutationSetKeyIndicateSyncDataTypeEnabled = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { dataType: KeyIndicateSyncDataType; isEnabled: boolean }>({
        mutationFn: async ({ dataType, isEnabled }) =>
            setKeyIndicateSyncDataTypeEnabled(dataType, isEnabled),
        onError: showKeyIndicateSyncSettingError,
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [QK.BACK_OFFICE.KEY_INDICATE_SYNC_DATA_TYPES],
            }),
    });
};

export const useMutationEnqueueAsrFullAnalysis = () =>
    useMutation<
        EnqueueAsrFullAnalysisResponse,
        Error,
        { tenantId: string; interactionPartExternalIds: string[] }
    >({
        mutationFn: ({ tenantId, interactionPartExternalIds }) =>
            enqueueAsrFullAnalysis(tenantId, interactionPartExternalIds),
        onError: showAsrEnqueueError,
    });

export const useQueryGetCsmUsers = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.CSM_USERS],
        queryFn: async () => getCsmUsers(),
        enabled,
    });
};

export const useQueryGetSysOpUsers = () => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl);

    return useQuery({
        queryKey: [QK.BACK_OFFICE.SYSOP_USERS],
        queryFn: async () => getSysOpUsers(),
        enabled,
    });
};

// Shows the backend's raw validation detail (e.g. "already exists in Keycloak") because these
// endpoints only live in BackOffice today (internal staff, technical detail is fine).
const showSysOpUserError = (error: unknown) => {
    const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
    useToastStore().addToast('error', detail ?? __('error-response'));
};

export const useMutationCreateSysOpUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestCreateSysOpUser) => createSysOpUser(data),
        onError: showSysOpUserError,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.BACK_OFFICE.SYSOP_USERS] });
        },
    });
};

export const useMutationChangeUserRole = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestChangeUserRole) => changeUserRole(data),
        onError: showSysOpUserError,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.BACK_OFFICE.SYSOP_USERS] });
        },
    });
};

export const useMutationSendInteractionsForAnalysisByInterval = () =>
    useMutation<
        { id: string },
        Error,
        {
            tenantId: string;
            start: string;
            end: string;
            reanalyzeAlreadyAnalyzed: boolean;
            channelIds: string[] | null;
            channelTypes: AsrChannelType[] | null;
        }
    >({
        mutationFn: ({
            tenantId,
            start,
            end,
            reanalyzeAlreadyAnalyzed,
            channelIds,
            channelTypes,
        }) =>
            sendInteractionsForAnalysisByInterval(
                tenantId,
                start,
                end,
                reanalyzeAlreadyAnalyzed,
                channelIds,
                channelTypes
            ),
        onError: showAsrEnqueueError,
    });

export const useQueryGetAsrJobs = (tenantId: MaybeRefOrGetter<string>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(() => !!account.value?.backOfficeApiBaseUrl && !!toValue(tenantId));

    const activeStatuses: AsrJobStatus[] = ['Queued', 'Running'];

    return useQuery({
        queryKey: computed(() => [QK.BACK_OFFICE.ASR_JOBS, toValue(tenantId)]),
        queryFn: () => getAsrJobs(toValue(tenantId)),
        enabled,
        refetchInterval: (query) => {
            const jobs = query.state.data;
            if (!Array.isArray(jobs)) return false;
            return jobs.some((j) => activeStatuses.includes(j.status)) ? 5000 : false;
        },
    });
};
